import { useRef, useState } from "react";
import { motion } from "framer-motion";
import html2canvas from "html2canvas";
import { X, Download, Lock } from "lucide-react";
import { useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";
import { Icon } from "@/lib/iconMap";
import {
  WORLDS, WORLD_IDS, CHARACTER_IDS, CHARACTER_EXTRAS, WORLD_EXTRAS,
  ACHIEVEMENTS, getCharacter, worldOfCharacter,
} from "@/data";

const Section = ({ title, titleKr, children }) => (
  <div className="mb-9">
    <h3 className="mb-4 font-display text-lg tracking-tight text-white">
      {title} <span className="kr text-sm font-normal text-white/45">{titleKr}</span>
    </h3>
    {children}
  </div>
);

const Slot = ({ unlocked, accent, label, body }) => (
  <div
    className={`rounded-2xl border p-4 transition-colors ${
      unlocked ? "bg-white/5" : "bg-black/30"
    }`}
    style={{ borderColor: unlocked ? `${accent}55` : "rgba(255,255,255,0.08)" }}
  >
    <p className="text-sm font-medium" style={{ color: unlocked ? accent : "rgba(255,255,255,0.4)" }}>
      {unlocked ? label : "???"}
    </p>
    {unlocked ? (
      <p className="mt-1 text-xs leading-relaxed text-white/65">{body}</p>
    ) : (
      <p className="mt-1 flex items-center gap-1 text-xs text-white/30">
        <Lock className="h-3 w-3" /> Locked
      </p>
    )}
  </div>
);

export default function MemoryAlbum({ onClose }) {
  const { state, completion } = useGame();
  const [sharing, setSharing] = useState(false);
  const cardRef = useRef(null);

  const giftName = (charId, itemId) => {
    const wid = worldOfCharacter(charId);
    const g = WORLD_EXTRAS[wid].gifts.find((x) => x.id === itemId);
    return g ? g.name : itemId;
  };

  const favoriteId =
    Object.entries(state.chatCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  const favorite = favoriteId ? getCharacter(favoriteId) : null;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    sound.playClick();
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#050505",
        scale: 2,
        logging: false,
      });
      const link = document.createElement("a");
      link.download = "my-webtoonverse-journey.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      /* noop */
    }
    setSharing(false);
  };

  return (
    <motion.div
      className="wv-scroll fixed inset-0 z-[110] overflow-y-auto bg-[#050505]/95 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="memory-album"
    >
      <div className="wv-grain" />
      <div className="relative mx-auto max-w-4xl px-6 py-12">
        {/* header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl tracking-tight text-white">
              Memory Album <span className="kr text-base font-normal text-white/45">기억 앨범</span>
            </h2>
            <p className="mt-1 text-sm text-white/55">
              WebtoonVerse Explorer — <span className="text-[#FF2A6D] font-semibold">{completion.pct}%</span> complete
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadCard}
              disabled={sharing}
              data-testid="share-journey"
              className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF2A6D] to-[#B14EDB] px-5 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105 disabled:opacity-60"
            >
              <Download className="h-4 w-4" /> {sharing ? "Saving…" : "Share my journey"}
            </button>
            <button
              onClick={onClose}
              data-testid="album-close"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/70 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Story Moments */}
        <Section title="Story Moments" titleKr="스토리 순간">
          <div className="grid gap-3 sm:grid-cols-3">
            {WORLDS.map((w) => {
              const choiceId = state.story[w.id];
              const choice = w.storyMoment.choices.find((c) => c.id === choiceId);
              return (
                <Slot
                  key={w.id}
                  unlocked={!!choiceId}
                  accent={w.accent}
                  label={`${w.name} · ${choice?.id || ""}`}
                  body={choice ? choice.result : ""}
                />
              );
            })}
          </div>
        </Section>

        {/* Character Secrets */}
        <Section title="Character Secrets" titleKr="캐릭터의 비밀">
          <div className="grid gap-3 sm:grid-cols-2">
            {CHARACTER_IDS.map((cid) => {
              const c = getCharacter(cid);
              const ex = CHARACTER_EXTRAS[cid];
              return (
                <Slot
                  key={cid}
                  unlocked={!!state.secrets[cid]}
                  accent={c.accent}
                  label={`${c.name}'s secret`}
                  body={ex.secret}
                />
              );
            })}
          </div>
        </Section>

        {/* Perfect Gifts */}
        <Section title="Perfect Gifts" titleKr="완벽한 선물">
          <div className="grid gap-3 sm:grid-cols-3">
            {CHARACTER_IDS.map((cid) => {
              const c = getCharacter(cid);
              const given = state.perfectGifts[cid];
              return (
                <Slot
                  key={cid}
                  unlocked={!!given}
                  accent={c.accent}
                  label={`${c.name} ♥ ${given ? giftName(cid, given) : ""}`}
                  body={given ? "The perfect gift — a memory they won't forget." : ""}
                />
              );
            })}
          </div>
        </Section>

        {/* Mini-games */}
        <Section title="Mini-games" titleKr="미니게임">
          <div className="grid gap-3 sm:grid-cols-3">
            {WORLDS.map((w) => (
              <Slot
                key={w.id}
                unlocked={!!state.minigames[w.id]}
                accent={w.accent}
                label={WORLD_EXTRAS[w.id].minigame.name}
                body={WORLD_EXTRAS[w.id].minigame.reward}
              />
            ))}
          </div>
        </Section>

        {/* Achievements */}
        <Section title="Achievements" titleKr="업적">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {ACHIEVEMENTS.map((ach) => {
              const got = !!state.achievements[ach.id];
              return (
                <div
                  key={ach.id}
                  className={`flex flex-col items-center rounded-2xl border p-4 text-center transition-all ${
                    got ? "border-[#FF2A6D]/40 bg-white/5" : "border-white/8 bg-black/30 opacity-50"
                  }`}
                  data-testid={`achievement-${ach.id}`}
                >
                  <Icon
                    name={ach.icon}
                    className="h-7 w-7"
                    style={{ color: got ? "#FF2A6D" : "rgba(255,255,255,0.3)" }}
                  />
                  <p className="mt-2 text-xs font-medium text-white">{got ? ach.title : "???"}</p>
                  <p className="kr mt-0.5 text-[0.65rem] text-white/45">{got ? ach.titleKr : ""}</p>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {/* hidden share card (rendered off-screen for html2canvas) */}
      <div style={{ position: "fixed", left: -9999, top: 0 }} aria-hidden="true">
        <div
          ref={cardRef}
          style={{
            width: 600,
            height: 800,
            background:
              "radial-gradient(circle at 25% 20%, rgba(255,42,109,0.35), transparent 45%), radial-gradient(circle at 80% 75%, rgba(177,78,219,0.3), transparent 50%), #050505",
            padding: 48,
            color: "#fff",
            fontFamily: "Outfit, sans-serif",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ fontFamily: "Unbounded, sans-serif", fontSize: 14, letterSpacing: 6, color: "#fff" }}>
            WEBTOONVERSE
          </div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 6 }}>
            웹툰 속으로 들어가다 — My Journey
          </div>

          <div style={{ marginTop: 56, textAlign: "center" }}>
            <div
              style={{
                fontFamily: "Unbounded, sans-serif",
                fontSize: 96,
                lineHeight: 1,
                background: "linear-gradient(120deg,#FF2A6D,#B14EDB,#05D9E8)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {completion.pct}%
            </div>
            <div style={{ fontSize: 14, letterSpacing: 3, color: "rgba(255,255,255,0.6)", marginTop: 8 }}>
              EXPLORER COMPLETION
            </div>
          </div>

          <div style={{ marginTop: 48, display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              ["Worlds visited", `${WORLD_IDS.filter((w) => state.visited[w]).length} / 3`],
              ["Secrets unlocked", `${CHARACTER_IDS.filter((c) => state.secrets[c]).length} / 6`],
              ["Mini-games cleared", `${WORLD_IDS.filter((w) => state.minigames[w]).length} / 3`],
              ["Story moments", `${WORLD_IDS.filter((w) => state.story[w]).length} / 3`],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.1)", paddingBottom: 10 }}>
                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 16 }}>{k}</span>
                <span style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: "auto", textAlign: "center" }}>
            {favorite && (
              <div style={{ marginBottom: 18 }}>
                <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>Favorite character</span>
                <div style={{ fontFamily: "Unbounded, sans-serif", fontSize: 22, color: "#FF2A6D", marginTop: 4 }}>
                  {favorite.name} · {favorite.koreanName}
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
              Step inside the story · webtoonverse
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
